import { Button, Group, Stack, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { IconPhotoPlus, IconTrash } from '@tabler/icons-react';
import { UPLOAD_IMAGE_MIME_TYPES } from '@creativitas/shared';
import { AssetImage } from '@/components/ui/AssetImage';
import { toUserMessage } from '@/lib/api';
import { uploadImage } from './content-api';

/**
 * Картинка, которую выбирают файлом, а не путём (§бэклог 11).
 *
 * Прежде здесь было поле для ручного ввода вроде
 * `/assets/tales/dragon/w840/scene.webp`. Это требовало знать, куда файл
 * положен и как называется, — знание, которого у автора материала нет и быть
 * не должно. Первая же попытка Дарьи сделать материал самой уткнулась в это.
 *
 * Теперь файл переносят в рамку, адрес складывается сам, и картинка сразу
 * видна: без предпросмотра ошибиться нечем, но и убедиться нечем тоже.
 */
export function ImageField({
  value,
  label,
  description,
  disabled,
  testId,
  onChange,
}: {
  value: string | null;
  label: string;
  description?: string;
  disabled?: boolean;
  /** Устойчивая зацепка для сквозных проверок: подпись поля — русская фраза. */
  testId?: string;
  /** Адрес загруженного файла или `null`, когда картинку убрали. */
  onChange: (url: string | null) => void;
}) {
  const upload = useMutation({
    mutationFn: (file: File) => uploadImage(file),
    onSuccess: (stored) => onChange(stored.url),
    onError: (error) =>
      notifications.show({
        color: 'red',
        title: 'Не удалось загрузить картинку',
        message: toUserMessage(error),
      }),
  });

  return (
    <Stack gap="xs" data-testid={testId}>
      <div>
        <Text size="sm" fw={600}>{label}</Text>
        {description ? (
          <Text size="xs" c="var(--text-muted)">{description}</Text>
        ) : null}
      </div>

      {value ? (
        <Stack gap={6} maw={320}>
          <AssetImage
            src={value}
            alt=""
            ratio="16 / 9"
            fit="contain"
            fallbackLabel="Файл не открывается"
          />
          <Group gap="xs">
            <Button
              size="compact-xs"
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={13} />}
              disabled={disabled || upload.isPending}
              onClick={() => onChange(null)}
            >
              Убрать
            </Button>
          </Group>
        </Stack>
      ) : null}

      {/*
        Рамка остаётся и при выбранной картинке: заменить — то же действие, что
        и выбрать, и отдельная кнопка «Заменить» была бы вторым способом сделать
        одно и то же.
      */}
      <Dropzone
        onDrop={(files) => files[0] && upload.mutate(files[0])}
        accept={[...UPLOAD_IMAGE_MIME_TYPES]}
        loading={upload.isPending}
        disabled={disabled}
        maxFiles={1}
        p="xs"
        radius="md"
        maw={320}
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <Group gap={8} justify="center" style={{ pointerEvents: 'none' }}>
          <IconPhotoPlus size={16} color="var(--text-muted)" />
          <Text size="xs" c="var(--text-muted)">
            {value ? 'Заменить картинку' : 'Перетащите картинку или нажмите'}
          </Text>
        </Group>
      </Dropzone>
    </Stack>
  );
}
